import { create } from 'zustand';

import type { ConflictMode } from '../api';

/** What is being put into a folder that already has items with the same names. */
export type ConflictOperation = 'upload' | 'move' | 'copy';

export interface ConflictRequest {
  operation: ConflictOperation;
  /** Names that exist in the target folder. */
  names: string[];
  /** How many items the operation has in total. */
  total: number;
  /** Uploads of files over existing files may become new versions. */
  allowVersion: boolean;
  /** Folder the items go to (shown in the text). */
  folderName: string;
}

interface Pending extends ConflictRequest {
  id: number;
  resolve: (mode: ConflictMode | null) => void;
}

interface ConflictState {
  current: Pending | null;
  queue: Pending[];
  push: (item: Pending) => void;
  settle: (id: number, mode: ConflictMode | null) => void;
}

let nextId = 1;

export const useConflictStore = create<ConflictState>()((set, get) => ({
  current: null,
  queue: [],
  push: (item) => {
    if (get().current) set((s) => ({ queue: [...s.queue, item] }));
    else set({ current: item });
  },
  settle: (id, mode) => {
    const { current, queue } = get();
    if (current?.id !== id) return;
    current.resolve(mode);
    const [next = null, ...rest] = queue;
    set({ current: next, queue: rest });
  },
}));

/**
 * Asks how to resolve name conflicts: keep both (rename), replace, skip — or a new version for uploads. Resolves with
 * the chosen mode, `null` when cancelled. Needs `<ConflictDialogHost />` (mounted by the disk's top-bar widget).
 */
export function askConflict(request: ConflictRequest): Promise<ConflictMode | null> {
  return new Promise((resolve) => {
    useConflictStore.getState().push({ ...request, id: nextId++, resolve });
  });
}
